import { mergeDeepRight } from 'ramda';
import { AvenyCollectionMap } from '../../../../../../../../Model/ModelContext/ModelContext';
import {
  safeGetAttributeModel,
  safeGetAssociationInfo,
} from '../../../../../../../../Model/avenyModelUtils';
import { findFormatter } from '../../../../../../../../Utils/Formatters/FindFormatter';
import {
  IAssignedSelectableColumnHeader,
  IConvertedClipboardValue,
  IInternalSelectableColumnHeader,
  IParseClipboardValue,
  ISelectableColumnHeader,
  isCellValueValidFunction,
} from '../ImportDialog';
import { ClientAttributeDataType } from '../../../../../../../../Types/ClientModelTypes';
import shortid from 'shortid';
import { MAX_NUMBER_OF_KONTODELAR } from '../../../../../../Leverantor/ObjektVyer/FakturaDel/Consts/FakturadelConsts';
import { GridValueFormatter } from '@mui/x-data-grid-pro';
import { ParseResult } from '../../../../../../../../Utils/Formatters/FormatterTypes';

const currFormatter = findFormatter('TEDFormatCurr');

/**
 * Turns a ISelectableColumnHeader to a IInternalSelectableColumnHeader with a label and valueformatter
 * @param {ISelectableColumnHeader} header - Header to be converted
 * @param {string} attributeModelFormatter - Current attributes formatter
 * @param {ClientAttributeDataType | undefined} attributeModelDataType - current attributes data type
 * @param {string} attributeModelFormatterDisplayParams - Current attributes formatter displayparams
 * @param {string} modelDisplayLabel - Displaylabel according to current model
 * @returns {IInternalSelectableColumnHeader}
 */
const getInternalSelectableColumnHeaderWithFormatter = (
  header: ISelectableColumnHeader,
  attributeModelFormatter: string | undefined,
  attributeModelDataType: ClientAttributeDataType | undefined,
  attributeModelFormatterDisplayParams: string | undefined,
  modelDisplayLabel: string
): IInternalSelectableColumnHeader => {
  const formatter = findFormatter(
    attributeModelFormatter,
    attributeModelDataType
  );

  const selectableColumn: IInternalSelectableColumnHeader = {
    ...header,
    label: header?.customLabel ?? modelDisplayLabel,
    valueFormatter: value =>
      formatter.displayer.display(
        value,
        true,
        attributeModelFormatterDisplayParams
      ),
  };
  return selectableColumn;
};
/**
 * Turns a ISelectableColumnHeader to a IInternalSelectableColumnHeader with a label and valueformatter based on association information
 * @param {ISelectableColumnHeader} header - Header to be converted
 * @param {string} currentCollectionName - Current collection
 * @param {string} associationName - The associations name
 * @param {AvenyCollectionMap} avenyCollectionMap - Avenymodel
 * @param {string | undefined} associationPath - Path to association based on current collection
 * @returns {IInternalSelectableColumnHeader}
 */
const getAssociatedSelectableColumnHeader = (
  header: ISelectableColumnHeader,
  currentCollectionName: string,
  associationName: string,
  avenyCollectionMap: AvenyCollectionMap,
  associationPath?: string
): IInternalSelectableColumnHeader => {
  const startOfPath = associationPath ? associationPath + '.' : '';
  const attributePath = startOfPath + associationName + '.' + header.attribute;

  const attributeModel = safeGetAttributeModel(
    currentCollectionName,
    attributePath,
    avenyCollectionMap
  );

  const associationModel = safeGetAssociationInfo(
    currentCollectionName,
    startOfPath + associationName,
    avenyCollectionMap
  )?.association;

  if (attributeModel == undefined || associationModel == undefined) {
    return {
      ...header,
      label: header?.customLabel ?? associationPath,
    } as IInternalSelectableColumnHeader;
  }

  return getInternalSelectableColumnHeaderWithFormatter(
    header,
    attributeModel.Formatter,
    attributeModel.DataType,
    attributeModel.FormatterDisplayParams,
    associationModel.DisplayLabel
  );
};

export const convertToInternalSelectableColumnHeader = (
  header: ISelectableColumnHeader,
  currentCollectionName: string,
  avenyCollectionMap: AvenyCollectionMap
): IInternalSelectableColumnHeader => {
  const associationName = header.associationName;
  const path = header.associationPath ?? [];
  const isAttribute = associationName == undefined;
  if (isAttribute) {
    const attributeResult = safeGetAttributeModel(
      currentCollectionName,
      header.attribute,
      avenyCollectionMap
    );

    if (attributeResult == undefined) {
      return {
        ...header,
        label: header?.customLabel ?? header.attribute,
      } as IInternalSelectableColumnHeader;
    }

    return getInternalSelectableColumnHeaderWithFormatter(
      header,
      attributeResult.Formatter,
      attributeResult.DataType,
      attributeResult.FormatterDisplayParams,
      attributeResult.DisplayLabel
    );
  } else if (!isAttribute) {
    if (path.length > 0) {
      const attributePath = path.join('.');

      return getAssociatedSelectableColumnHeader(
        header,
        currentCollectionName,
        associationName,
        avenyCollectionMap,
        attributePath
      );
    } else {
      return getAssociatedSelectableColumnHeader(
        header,
        currentCollectionName,
        associationName,
        avenyCollectionMap
      );
    }
  }

  return {
    ...header,
    label: header?.customLabel ?? header.attribute,
  } as IInternalSelectableColumnHeader;
};

const updateGridAndCollectionData = (
  prevGridData: Record<string, any>,
  prevCollectionData: Record<string, any>,
  column: IInternalSelectableColumnHeader,
  cellData: any,
  row: any
) => {
  const isValueOkay = column?.isCellValueValid
    ? column?.isCellValueValid?.(cellData, row, column)
    : { validValue: true, errorMessage: '' };

  let newGridData = {
    ...prevGridData,
    [column.attribute]: cellData,
  };

  let newCollectionData = {
    ...prevCollectionData,
    [column.attribute]: cellData,
  };

  // Bör kunna hantera djupare än en association skulle tex kunna använda sig utav associationPath eller liknande
  if (column.associationName) {
    if (column.associationPath && column.associationPath.length > 0) {
      newGridData = {
        ...prevGridData,
        [column.associationPath.join('.') +
        '.' +
        column.associationName +
        '.' +
        column.attribute]: cellData,
      };

      let collectionData = {};
      for (let index = column.associationPath.length - 1; index > -1; index--) {
        const isFirst = column.associationPath.length - 1 === index;
        const associationName = column.associationPath[index];

        if (isFirst && column.associationName) {
          const associationDataObject = {
            [column.associationName]: {
              [column.attribute]: cellData,
            },
          };

          const newAssociationData = {
            [associationName]: {
              _Associations: { ...associationDataObject },
            },
          };

          collectionData = { ...newAssociationData };
        } else {
          const newAssociationData = {
            [associationName]: {
              _Associations: { ...collectionData },
            },
          };
          collectionData = newAssociationData;
        }
      }
      const mergedAssociations = mergeDeepRight(
        collectionData,
        prevCollectionData._Associations
      );

      newCollectionData = {
        ...prevCollectionData,
        _Associations: mergedAssociations,
      };
    } else {
      const associationData = {
        [column.associationName]: {
          [column.attribute]: cellData,
        },
      };

      const mergedAssociations = mergeDeepRight(
        associationData,
        prevCollectionData._Associations
      );

      newCollectionData = {
        ...prevCollectionData,
        _Associations: mergedAssociations,
      };

      newGridData = {
        ...prevGridData,
        [column.associationName + '.' + column.attribute]: cellData,
      };
    }
  }

  if (!isValueOkay.validValue && column?.isCellValueValid != undefined) {
    const path =
      column.associationPath && column.associationPath.length > 0
        ? column.associationPath.join('.') + '.'
        : '';
    const startOfPath = column.associationName
      ? path + column.associationName + '.'
      : path;

    const attributePath = startOfPath + column.attribute;

    return {
      collectionData: { ...prevCollectionData },
      gridData: {
        ...newGridData,
        ErrorMessage:
          prevGridData?.ErrorMessage != undefined
            ? prevGridData.ErrorMessage + ', ' + isValueOkay.errorMessage
            : isValueOkay.errorMessage,
        ErrorColumns:
          prevGridData?.ErrorColumns != undefined
            ? prevGridData.ErrorColumns + ',' + attributePath
            : attributePath,
      },
    };
  }

  const updatedData = {
    collectionData: newCollectionData,
    gridData: newGridData,
  };

  return updatedData;
};

export const convertImportedRows = (
  rows: {
    id: string;
    index: number;
    [key: string]: any;
  }[],
  assignedColumnHeaders: IAssignedSelectableColumnHeader[],
  selectableColumnHeaders: IInternalSelectableColumnHeader[]
): IConvertedClipboardValue[] => {
  const convertedRows = rows?.map((row, index) => {
    const rowData = assignedColumnHeaders.reduce(
      (prev, column) => {
        if (column.selectedForIndex != null) {
          const cellData = row?.[column.selectedForIndex]?.trim();

          if (cellData === undefined || cellData === null || cellData === '') {
            return prev;
          }

          const prevCollectionData = prev.collectionData;
          const prevGridData = prev.gridData;

          const isKodstrang = column.attribute === 'Kodsträng';
          if (isKodstrang) {
            const hasComma = (cellData as string).includes(',');

            const kodstrangHyphenSplitValues = (cellData as string).split('-');

            const kodstrangCommaSplitValues = hasComma
              ? kodstrangHyphenSplitValues.flatMap(splitData =>
                  splitData?.split(',')
                )
              : kodstrangHyphenSplitValues;

            const kodstrang = kodstrangCommaSplitValues
              .slice(0, MAX_NUMBER_OF_KONTODELAR)
              .reduce(
                (accumulatedData, kodstrangPart, i) => {
                  const foundHeader = selectableColumnHeaders.find(header => {
                    return header.associationName === `Kontodel${i + 1}`;
                  });
                  const trimmedKodstrangPart = kodstrangPart.trim();
                  if (foundHeader && trimmedKodstrangPart !== '') {
                    // Initial value is two keys to be able to distinguish between first and next iterations
                    if (Object.keys(accumulatedData.gridData).length === 2) {
                      const updatedData = updateGridAndCollectionData(
                        prevGridData,
                        prevCollectionData,
                        foundHeader,
                        trimmedKodstrangPart,
                        row
                      );

                      return updatedData;
                    } else {
                      const updatedData = updateGridAndCollectionData(
                        accumulatedData.gridData,
                        accumulatedData.collectionData,
                        foundHeader,
                        trimmedKodstrangPart,
                        row
                      );

                      return updatedData;
                    }
                  }
                  return accumulatedData;
                },
                {
                  gridData: {
                    ErrorMessage: undefined,
                    ErrorColumns: undefined,
                  },
                  collectionData: { _Associations: {} },
                } as {
                  gridData: Record<string, any>;
                  collectionData: Record<string, any>;
                }
              );
            return kodstrang;
          } else {
            const updatedData = updateGridAndCollectionData(
              prevGridData,
              prevCollectionData,
              column,
              cellData,
              row
            );

            return updatedData;
          }
        }
        return prev;
      },
      {
        gridData: { ErrorMessage: undefined, ErrorColumns: undefined },
        collectionData: { _Associations: {} },
      } as {
        gridData: Record<string, any>;
        collectionData: Record<string, any>;
      }
    );

    return {
      id: row.id,
      index,
      ...rowData.gridData,
      collectionData: { ...rowData.collectionData },
    };
  });

  const filteredConvertedRows = convertedRows?.filter(
    row =>
      !Object.entries(row).every(object => {
        const key = object[0];
        const value = object[1];

        if (key === 'id') {
          return true;
        }
        if (key === 'collectionData') {
          return true;
        }

        return value == null || value == '';
      })
  );

  return filteredConvertedRows;
};

export const parseClipboard = (text: string): IParseClipboardValue[] => {
  const rows: IParseClipboardValue[] = text
    .split('\r\n')
    .reduce<IParseClipboardValue[]>((prev, row, index) => {
      if (row.trim() == '') {
        return prev;
      }
      const rowData = row.split('\t').map(column => {
        return column;
      });

      return [...prev, { id: shortid.generate(), index, ...rowData }];
    }, []);
  return rows;
};

export const getAssignedSelectableColumnHeaders = (
  columnHeader: IAssignedSelectableColumnHeader,
  invalidCellClassName: string
) => {
  const customColumnOptions = columnHeader?.customColumnOptions
    ? {
        ...columnHeader?.customColumnOptions,
        valueFormatter:
          columnHeader.customColumnOptions?.valueFormatter ??
          columnHeader?.valueFormatter,
      }
    : { valueFormatter: columnHeader?.valueFormatter };

  const field = columnHeader.associationName
    ? columnHeader.associationPath && columnHeader.associationPath.length > 0
      ? columnHeader.associationPath.join('.') +
        '.' +
        columnHeader.associationName +
        '.' +
        columnHeader.attribute
      : columnHeader.associationName + '.' + columnHeader.attribute
    : columnHeader.attribute;

  return {
    field: field,
    headerName: columnHeader.label,
    ...customColumnOptions,
    cellClassName: (params: any) => {
      if (params.row?.ErrorColumns?.includes(params.field)) {
        return invalidCellClassName;
      }
      return '';
    },
  };
};

export const isNumberCellValueValid: isCellValueValidFunction = (
  value: string,
  _: any,
  column: IInternalSelectableColumnHeader
) => {
  const parsedValue = parseFloat(value);
  if (isNaN(parsedValue)) {
    return {
      validValue: false,
      errorMessage: `Värdet för ${column.label} är inte ett giltigt nummer`,
    };
  }
  return { validValue: true, errorMessage: '' };
};

export const currencyValueFormatter: GridValueFormatter<
  any,
  any,
  any,
  string
> = value => {
  const valueAsNumber = currFormatter.parser.parse(value); // To ensure correct parsing of different number formats
  if (valueAsNumber.result !== ParseResult.OK) {
    return value;
  }
  return currFormatter.displayer.display(
    valueAsNumber.value,
    true,
    'DisplayZeroAsBlank="Y"'
  );
};
